import { Box, Dialog, DialogContent, DialogTitle, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, Typography } from '@mui/material';
import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';

import { getFormattedDate } from '../components/utilities';
import GameService from '../../../services/game.service';
import { ActionData, isGoalTag } from '../components/common';
import { getPeriod } from '../games/tabs/overview/tagListItem';
import TeamStatsVideoPlayer from '../teams/tabs/stats/videoDialog';
import GameExportToEdits from '../games/tabs/overview/exportEdits';

import '../coach_style.css';

// Column groups with their properties for field players
const columnGroups = [
    {
        name: 'Attacking',
        color: '#ed6c02',
        columns: [
            { id: 'total_goal', alt: 'total_goals', title: 'Goals', action: 'Goal', highlight: 'green' },
            { id: 'total_shot', alt: 'total_shots', title: 'Shots', action: 'GoalKick' },
            { id: 'total_dribble', alt: 'total_dribbles', title: 'Dribble', action: 'Dribble' },
            { id: 'total_crosses', title: 'Cross', action: 'Cross' }
        ]
    },
    {
        name: 'Passing',
        color: '#2e7d32',
        columns: [
            { id: 'total_passes', title: 'Total', action: 'Passes' },
            { id: 'total_pass_success', alt: 'total_successful_passes', title: 'Success', action: 'PassesSuccess' },
            { id: 'total_corner', alt: 'total_corners', title: 'Corner', action: 'Corner' },
            { id: 'total_free_kick', alt: 'total_free_kicks', title: 'FK', action: 'FreeKick' }
        ]
    },
    {
        name: 'Defensive',
        color: '#1976d2',
        columns: [
            { id: 'total_interception', alt: 'total_interceptions', title: 'Interceptions', action: 'Interception' },
            { id: 'total_tackle', alt: 'total_tackles', title: 'Tackle', action: 'Tackle' },
            { id: 'total_blocked', title: 'Block', action: 'Blocked' },
            { id: 'total_clearance', alt: 'total_clearances', title: 'Clear', action: 'Clearance' }
        ]
    },
    {
        name: 'Discipline',
        color: '#7b1fa2',
        columns: [
            { id: 'total_turnover', alt: 'total_turnovers', title: 'TO', action: 'Turnover', highlight: 'red' },
            { id: 'total_fouls', title: 'Fouls', action: 'Foul', highlight: 'red' },
            { id: 'total_draw_fouls', title: 'Drawn', action: 'DrawFoul' }
        ]
    }
];

// Goalkeeper-specific columns (only shown if player has GK stats)
// Note: Exits column is excluded as it's specific to the dedicated Goalkeepers page
const goalkeeperColumns = {
    name: 'Goalkeeper',
    color: '#0288d1',
    columns: [
        { id: 'total_saved', title: 'Saved', action: 'Saved' },
        { id: 'total_super_save', alt: 'total_super_saved', title: 'Super Saved', action: 'SuperSaved' },
        { id: 'total_goal_received', alt: 'total_goals_received', title: 'Goals Received', action: 'GoalReceive', highlight: 'red' },
        { id: 'total_air_challenge', alt: 'total_air_challenges', title: 'Air Challenges', action: 'AirChallenge' },
        { id: 'total_ground_challenge', alt: 'total_ground_challenges', title: 'Ground Challenges', action: 'GroundChallenge' },
        { id: 'total_one_vs_one', title: '1v1', action: 'One' }
    ]
};

const PlayersGamesDialog = ({ open, onClose, list, playerName, t }) => {
    const { user: currentUser } = useSelector((state) => state.auth);

    const [playData, setPlayData] = useState([]);
    const [videoOpen, setVideoOpen] = useState(false);
    const [gameList, setGameList] = useState([]);
    const [exportOpen, setExportOpen] = useState(false);
    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('game_date');

    // Check if player has any goalkeeper stats
    const hasGoalkeeperStats = useMemo(() => {
        if (!list || list.length === 0) return false;
        return list.some(game =>
            goalkeeperColumns.columns.some(col => {
                const value = game[col.id] ?? game[col.alt] ?? 0;
                return value > 0;
            })
        );
    }, [list]);

    // Build active column groups based on data
    const activeColumnGroups = useMemo(() => {
        const groups = [...columnGroups];
        if (hasGoalkeeperStats) {
            groups.push(goalkeeperColumns);
        }

        // Filter each group's columns to only include those with data
        return groups.map(group => ({
            ...group,
            columns: group.columns.filter(col =>
                list.some(game => {
                    const value = game[col.id] ?? game[col.alt] ?? 0;
                    return value > 0;
                })
            )
        })).filter(group => group.columns.length > 0);
    }, [list, hasGoalkeeperStats]);

    // Flatten all active columns for sorting
    const allColumns = useMemo(() =>
        activeColumnGroups.flatMap(group => group.columns),
        [activeColumnGroups]
    );

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    // Get numeric value for sorting
    const getNumericValue = (item, prop) => {
        if (prop === 'game_date') return new Date(item.game_date).getTime();
        const propObj = allColumns.find(p => p.id === prop);
        if (!propObj) return 0;
        return item[propObj.id] ?? item[propObj.alt] ?? 0;
    };

    // Sort the list
    const sortedList = [...list].sort((a, b) => {
        const aVal = getNumericValue(a, orderBy);
        const bVal = getNumericValue(b, orderBy);
        if (order === 'asc') return aVal - bVal;
        return bVal - aVal;
    });

    // Calculate totals
    const totals = useMemo(() => {
        const result = {};
        allColumns.forEach(col => {
            result[col.id] = list.reduce((sum, item) => {
                const value = item[col.id] ?? item[col.alt] ?? 0;
                return sum + (typeof value === 'number' ? value : 0);
            }, 0);
        });
        return result;
    }, [list, allColumns]);

    const handleDisplayVideo = async (cell, prop) => {
        const value = cell[prop.id] ?? cell[prop.alt] ?? 0;
        if (value > 0 && prop.action !== '') {
            // Get game details for video URL
            const gameRes = await GameService.getGameById(cell.game_id);
            const video_url = gameRes?.video_url || gameRes?.result?.video_url || '';

            // Format as array for TeamStatsVideoPlayer (same format used across all pages)
            setGameList([{ id: cell.game_id, video_url }]);

            await GameService.getGamePlayerTags(
                currentUser.id,
                cell.team_id,
                `${cell.player_id}`,
                `${cell.game_id}`,
                ActionData[prop.action].action_id,
                ActionData[prop.action].action_type_id,
                ActionData[prop.action].action_result_id,
                null,
                null,
                null,
                null,
                null
            ).then((res) => {
                // Goal clips are shots only; assists are also tagged result=Goal, so filter them out.
                const tags = prop.action === 'Goal' ? res.filter(isGoalTag) : res;
                setPlayData(
                    tags.map((item) => {
                        return {
                            tag_id: item.id,
                            start_time: item.player_tag_start_time,
                            end_time: item.player_tag_end_time,
                            player_name: item.player_names || item.player_name || item.full_name || '',
                            action_name: item.action_names || item.action_name || '',
                            action_type: item.action_type_names || item.action_type_name || item.action_type || '',
                            action_result: item.action_result_names || item.action_result_name || item.action_result || '',
                            game_id: item.game_id || cell.game_id,
                            team_id: cell.team_id,
                            court_area: item.court_area_id,
                            inside_pain: item.inside_the_pain,
                            period: item.period,
                            time_in_game: item.time_in_game,
                            home_team_logo: item.home_team_logo,
                            away_team_logo: item.away_team_logo,
                            home_team_goals: item.home_team_goals ?? item.home_team_goal,
                            away_team_goals: item.away_team_goals ?? item.away_team_goal
                        };
                    })
                );

                if (tags.length > 0) setVideoOpen(true);
            });
        }
    };

    const handleExportTags = (cell, prop) => (e) => {
        e.preventDefault();

        const value = cell[prop.id] ?? cell[prop.alt] ?? 0;
        if (value > 0 && prop.action !== '') {
            GameService.getGamePlayerTags(
                currentUser.id,
                cell.team_id,
                `${cell.player_id}`,
                `${cell.game_id}`,
                ActionData[prop.action].action_id,
                ActionData[prop.action].action_type_id,
                ActionData[prop.action].action_result_id,
                null,
                null,
                null,
                null,
                null
            ).then((res) => {
                setPlayData(res);

                if (res.length > 0) setExportOpen(true);
            });
        }
    };

    // Get opponent team name - handles different field naming conventions
    const getOpponentName = (item) => {
        if (item.opponent_name) return item.opponent_name;
        if (item.team_name === item.home_team_name) return item.away_team_name || 'Unknown';
        return item.home_team_name || 'Unknown';
    };

    // Get value from item with fallback to alternative field name
    const getValue = (item, prop) => {
        const value = item[prop.id] ?? item[prop.alt];
        return value !== undefined && value !== null ? value : 0;
    };

    // Check if value exists for clicking
    const hasValue = (item, prop) => {
        const value = item[prop.id] ?? item[prop.alt] ?? 0;
        return value > 0;
    };

    // Get cell style based on column properties
    const getCellStyle = (prop, value, isTotal = false) => {
        const baseStyle = {
            cursor: value > 0 && prop.action !== '' && !isTotal ? 'pointer' : 'default',
            fontSize: '13px',
            fontWeight: isTotal ? 600 : 400
        };

        // Highlight specific columns
        if (prop.highlight === 'red' && value > 0) {
            return { ...baseStyle, color: '#d32f2f', fontWeight: 600 };
        }
        if (prop.highlight === 'green' && value > 0) {
            return { ...baseStyle, color: '#2e7d32', fontWeight: 600 };
        }

        return baseStyle;
    };

    return (
        <Dialog open={open} onClose={onClose} fullScreen>
            <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6">{`Games of ${playerName}`}</Typography>
                    <IconButton onClick={onClose} sx={{ color: 'red' }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', p: 2, pt: 0 }}>
                <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                    <Table stickyHeader size="small" sx={{ '& .MuiTableCell-root': { padding: '6px 8px' } }}>
                        <TableHead>
                            {/* Group headers row */}
                            <TableRow>
                                <TableCell
                                    rowSpan={2}
                                    align="center"
                                    sx={{
                                        backgroundColor: '#f5f5f5',
                                        borderRight: '2px solid #e0e0e0',
                                        minWidth: 200,
                                        position: 'sticky',
                                        left: 0,
                                        top: 0,
                                        zIndex: 4,
                                        height: 66
                                    }}
                                >
                                    <TableSortLabel
                                        active={orderBy === 'game_date'}
                                        direction={orderBy === 'game_date' ? order : 'asc'}
                                        onClick={() => handleRequestSort('game_date')}
                                    >
                                        {t('Game')}
                                    </TableSortLabel>
                                </TableCell>
                                {activeColumnGroups.map((group, idx) => (
                                    <TableCell
                                        key={group.name}
                                        colSpan={group.columns.length}
                                        align="center"
                                        sx={{
                                            backgroundColor: group.color,
                                            color: 'white',
                                            fontWeight: 600,
                                            borderRight: idx < activeColumnGroups.length - 1 ? '2px solid #e0e0e0' : 'none',
                                            fontSize: '12px',
                                            position: 'sticky',
                                            top: 0,
                                            zIndex: 2
                                        }}
                                    >
                                        {t(group.name)}
                                    </TableCell>
                                ))}
                            </TableRow>
                            {/* Column headers row */}
                            <TableRow>
                                {activeColumnGroups.map((group, groupIdx) =>
                                    group.columns.map((col, colIdx) => (
                                        <TableCell
                                            key={col.id}
                                            align="center"
                                            sortDirection={orderBy === col.id ? order : false}
                                            sx={{
                                                backgroundColor: '#fafafa',
                                                borderRight: colIdx === group.columns.length - 1 && groupIdx < activeColumnGroups.length - 1 ? '2px solid #e0e0e0' : 'none',
                                                fontSize: '11px',
                                                minWidth: 50,
                                                position: 'sticky',
                                                top: 33,
                                                zIndex: 2
                                            }}
                                        >
                                            <TableSortLabel
                                                active={orderBy === col.id}
                                                direction={orderBy === col.id ? order : 'asc'}
                                                onClick={() => handleRequestSort(col.id)}
                                            >
                                                {t(col.title)}
                                            </TableSortLabel>
                                        </TableCell>
                                    ))
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedList.map((item, index) => (
                                <TableRow
                                    key={index}
                                    hover
                                    sx={{
                                        backgroundColor: index % 2 === 0 ? 'white' : '#fafafa',
                                        '&:hover': { backgroundColor: '#e3f2fd' }
                                    }}
                                >
                                    <TableCell
                                        align="left"
                                        sx={{
                                            borderRight: '2px solid #e0e0e0',
                                            position: 'sticky',
                                            left: 0,
                                            backgroundColor: index % 2 === 0 ? 'white' : '#fafafa',
                                            zIndex: 1
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                            <Typography variant="caption" sx={{ color: '#666', fontSize: '10px' }}>
                                                {item.season_name && item.league_name ? `${item.season_name}, ${item.league_name}` : ''}
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '12px' }}>
                                                {`${getFormattedDate(item.game_date)} vs ${getOpponentName(item)}`}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    {activeColumnGroups.map((group, groupIdx) =>
                                        group.columns.map((col, colIdx) => {
                                            const value = getValue(item, col);
                                            return (
                                                <TableCell
                                                    key={`${index}-${col.id}`}
                                                    align="center"
                                                    sx={{
                                                        ...getCellStyle(col, value),
                                                        borderRight: colIdx === group.columns.length - 1 && groupIdx < activeColumnGroups.length - 1 ? '2px solid #e0e0e0' : 'none'
                                                    }}
                                                    onClick={() => hasValue(item, col) && handleDisplayVideo(item, col)}
                                                    onContextMenu={handleExportTags(item, col)}
                                                >
                                                    {value}
                                                </TableCell>
                                            );
                                        })
                                    )}
                                </TableRow>
                            ))}
                            {/* Totals row */}
                            <TableRow sx={{ backgroundColor: '#e8e8e8' }}>
                                <TableCell
                                    align="left"
                                    sx={{
                                        borderRight: '2px solid #e0e0e0',
                                        position: 'sticky',
                                        left: 0,
                                        backgroundColor: '#e8e8e8',
                                        zIndex: 1,
                                        fontWeight: 600
                                    }}
                                >
                                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '12px' }}>
                                        {t('Total')} ({list.length} {t('games')})
                                    </Typography>
                                </TableCell>
                                {activeColumnGroups.map((group, groupIdx) =>
                                    group.columns.map((col, colIdx) => {
                                        const value = totals[col.id] || 0;
                                        return (
                                            <TableCell
                                                key={`total-${col.id}`}
                                                align="center"
                                                sx={{
                                                    ...getCellStyle(col, value, true),
                                                    borderRight: colIdx === group.columns.length - 1 && groupIdx < activeColumnGroups.length - 1 ? '2px solid #e0e0e0' : 'none',
                                                    backgroundColor: '#e8e8e8'
                                                }}
                                            >
                                                {value}
                                            </TableCell>
                                        );
                                    })
                                )}
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
                <TeamStatsVideoPlayer t={t} open={videoOpen} onClose={() => setVideoOpen(false)} video_url={gameList} tagList={playData} />
                <GameExportToEdits t={t} open={exportOpen} onClose={() => setExportOpen(false)} tagList={playData} isTeams={false} />
            </DialogContent>
        </Dialog>
    );
};

export default PlayersGamesDialog;
